import * as React from "react";

import { datadogRum } from "@datadog/browser-rum";

import { reloadPage } from "utils/reload-page.util";

// LD flags are only readable from React hooks, but the import factory runs
// outside React — Routes syncs the chunkLoadRecovery flag into this module.
let chunkLoadRecoveryEnabled = false;

export function setChunkLoadRecoveryEnabled(enabled: boolean) {
  chunkLoadRecoveryEnabled = enabled;
}

// Chunk-load failure phrasings across Chrome / Firefox / Safari, plus Vite's
// CSS preload failure. RUM (ENG-1479) shows all three browsers affected.
const CHUNK_LOAD_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /loading chunk [\d\w-]* failed/i,
  /unable to preload css/i
];

// Shared copy for the error boundaries' chunk-failure state (a surviving
// chunk error after the auto-reload usually means a device-local network
// problem, not a deploy).
export const CHUNK_LOAD_ERROR_MESSAGE =
  "We couldn't load this page — this usually means a network issue on this device. Check your connection and click Retry.";

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error) || !error.message) return false;
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) =>
    pattern.test(error.message)
  );
}

const storageKey = (chunkName: string) => `suno_chunk_reload:${chunkName}`;

function getMarker(chunkName: string): string | null {
  try {
    return sessionStorage.getItem(storageKey(chunkName));
  } catch {
    return null;
  }
}

function setMarker(chunkName: string): boolean {
  try {
    sessionStorage.setItem(storageKey(chunkName), new Date().toISOString());
    return true;
  } catch {
    return false;
  }
}

function clearMarker(chunkName: string) {
  try {
    sessionStorage.removeItem(storageKey(chunkName));
  } catch {
    // ignore — worst case the next failure rethrows instead of reloading
  }
}

/**
 * Wraps a lazy-route import so a deploy-time chunk hash mismatch recovers
 * with exactly one automatic reload per chunk (guarded by sessionStorage).
 * A second failure rethrows to the route error boundary. Non-chunk errors
 * always rethrow untouched.
 */
export function createRetryingImport<T>(
  importFn: () => Promise<T>,
  chunkName: string
): () => Promise<T> {
  return async () => {
    try {
      const module = await importFn();
      if (getMarker(chunkName)) {
        clearMarker(chunkName);
        datadogRum.addAction("chunk_load_recovered", { chunkName });
      }
      return module;
    } catch (error) {
      if (!chunkLoadRecoveryEnabled || !isChunkLoadError(error)) {
        throw error;
      }
      if (getMarker(chunkName)) {
        datadogRum.addAction("chunk_load_failed_after_reload", {
          chunkName,
          errorMessage: (error as Error).message
        });
        throw error;
      }
      // Never reload without a persisted guard — that risks a reload loop.
      if (!setMarker(chunkName)) {
        throw error;
      }
      datadogRum.addAction("chunk_reload_attempt", {
        chunkName,
        attempt: 1,
        errorMessage: (error as Error).message
      });
      reloadPage();
      // Keep the import pending so Suspense shows its fallback while the
      // page reloads, instead of flashing the error boundary.
      return new Promise<T>(() => {});
    }
  };
}

const PRELOAD_MARKER_PREFIX = "vite-preload:";

// Build SHA baked in by Vite's `define`; absent under Jest / non-Vite tooling.
function getAppVersion(): string {
  return typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "dev";
}

const preloadChunkName = () => `${PRELOAD_MARKER_PREFIX}${getAppVersion()}`;

const extractChunkUrl = (event: Event): string | undefined => {
  // Vite attaches the original error as `payload` on the event.
  const payload = (event as Event & { payload?: unknown }).payload;
  if (!(payload instanceof Error)) return undefined;
  return payload.message.match(/https?:\/\/[^\s)]+/)?.[0];
};

/**
 * Drop reload markers left behind by OTHER app versions so each deploy gets
 * a fresh auto-reload budget. Called once at boot (index.tsx). Deliberately
 * does NOT clear the current version's marker: the boot right after an
 * auto-reload looks "successful" before the chunk fails again, so clearing
 * on every boot would re-arm the DEV-2147 reload loop.
 */
export function pruneStaleChunkReloadMarkers() {
  try {
    const fullPrefix = storageKey(PRELOAD_MARKER_PREFIX);
    const currentKey = storageKey(preloadChunkName());
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(fullPrefix) && key !== currentKey) {
        sessionStorage.removeItem(key);
      }
    }
  } catch {
    // ignore — stale markers only delay a future auto-reload, never loop
  }
}

/**
 * Guarded replacement for the bare `vite:preloadError -> reload` listener
 * (SN-8541). A persistent local fetch failure (proxy/AV/flaky network) used
 * to reload forever, locking the user out (DEV-2147). Exactly one automatic
 * reload per app version per tab (sessionStorage, version-keyed so the next
 * deploy self-heals again); once the budget is spent the error propagates,
 * landing in lazyWithRetry / the route error boundaries.
 *
 * Deliberately NOT gated behind chunkLoadRecovery: the loop happens during
 * early boot, before LaunchDarkly resolves (flags default false there), so a
 * flag-gated cap would be off exactly when it's needed. The cap is a safety
 * fix, strictly safer than the unconditional reload in every flag state.
 */
export function handleVitePreloadError(event: Event) {
  const chunkName = preloadChunkName();
  if (getMarker(chunkName)) {
    datadogRum.addAction("chunk_reload_exhausted", {
      appVersion: getAppVersion(),
      chunkUrl: extractChunkUrl(event)
    });
    // No preventDefault: Vite rethrows, the failing import rejects into the
    // error boundaries (friendly retry screen) instead of reloading again.
    return;
  }
  // Never reload without a persisted guard — that risks a reload loop.
  if (!setMarker(chunkName)) {
    return;
  }
  datadogRum.addAction("chunk_reload_attempt", {
    appVersion: getAppVersion(),
    attempt: 1,
    chunkUrl: extractChunkUrl(event)
  });
  // Suppress Vite's rethrow only when we handle the failure with a reload.
  event.preventDefault();
  reloadPage();
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React.lazy's own constraint
export function lazyWithRetry<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  chunkName: string
) {
  return React.lazy(createRetryingImport(importFn, chunkName));
}
